import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faUser, faCalendarAlt, faVenusMars, faEnvelope, faPhone,
  faMapMarkerAlt, faCheckCircle, faExclamationTriangle,
  faPlus, faEdit, faTrash, faSyncAlt, faGraduationCap, faBriefcase,
  faLanguage, faFilter, faTimes, faIdCard, faEye, faFilePdf,
  faBolt, faCircle, faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { Link } from 'react-router-dom';

// --- Cache Dexie 24h avec fallback périmé ---
const db = new Dexie('CandidatsCacheDB');
db.version(1).stores({ candidats: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedCandidats(key) {
  const entry = await db.candidats.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_DURATION) return entry.data;
  return null;
}
async function setCachedCandidats(key, data) {
  await db.candidats.put({ key, data, timestamp: Date.now() });
}
async function getStaleCandidats(key) {
  const entry = await db.candidats.get(key);
  return entry ? entry.data : null;
}
async function clearCache(key) {
  await db.candidats.delete(key);
}

const API_URL = `${API_CONFIG2.apiUrl}candidat/api_candidat.php`;

const ManageCandidats = () => {
  const [candidats, setCandidats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ statut: '', sexe: '' });
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);
  const [cvFile, setCvFile] = useState(null);

  // --- Modal ---
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    nom: '', prenom: '', date_naissance: '', sexe: '', email: '', tel: '',
    adresse: '', cvUrl: '', competence: '', statut: '',
    niveau_etude: '', experience: '', langues: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // --- Nettoyage ---
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // --- Fonction de récupération avec cache, dédoublonnage et fallback ---
  const fetchCandidats = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (!isMounted.current) return;

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        setToast({ show: true, message: 'Erreur : trop de tentatives. Veuillez réessayer plus tard.', variant: 'danger' });
        setTimeout(() => setToast({ show: false }), 5000);
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      if (isMounted.current) setRefreshing(true);
      // On ne met pas loading à true pour garder les données existantes
    } else {
      if (isMounted.current) setLoading(true);
    }

    try {
      const filterKey = `statut_${filters.statut || ''}_sexe_${filters.sexe || ''}`;
      const cacheKey = `page_${page}_search_${search}_${filterKey}`;
      const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

      // 1. Cache frais (sauf forceRefresh)
      if (!forceRefresh) {
        const cached = await getCachedCandidats(cacheKey);
        if (cached && isMounted.current) {
          setCandidats(cached.data);
          setPagination(cached.pagination);
          setDataSource('cache');
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      // 2. Requête réseau
       const uniqueParam = new Date().getTime();
      let url = `${API_URL}?page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}&unique=${uniqueParam}`;
      if (filters.statut) url += `&statut=${encodeURIComponent(filters.statut)}`;
      if (filters.sexe) url += `&sexe=${encodeURIComponent(filters.sexe)}`;

      const res = await fetch(url, { signal: controller.signal });

      // Gestion 429
      if (res.status === 429) {
        // Essayer le cache périmé
        const stale = await getStaleCandidats(cacheKey);
        if (stale && isMounted.current) { 
          setCandidats(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          setLoading(false);
          setRefreshing(false);
          setToast({ show: true, message: '429 – Utilisation du cache périmé', variant: 'warning' });
          setTimeout(() => setToast({ show: false }), 5000);
          return;
        }
        // Sinon, réessayer avec backoff
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        setToast({ show: true, message: `429 – Nouvel essai dans ${waitTime/1000}s`, variant: 'warning' });
        setTimeout(() => setToast({ show: false }), 5000);
        setTimeout(() => {
          fetchCandidats(page, search, forceRefresh, retryCount + 1);
        }, waitTime);
        return;
      }

      if (!res.ok) {
        // Fallback cache périmé
        const stale = await getStaleCandidats(cacheKey);
        if (stale && isMounted.current) {
          setCandidats(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          setLoading(false);
          setRefreshing(false);
          setToast({ show: true, message: `Erreur réseau – Utilisation du cache`, variant: 'warning' });
          setTimeout(() => setToast({ show: false }), 5000);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      if (data.status === 'success') {
        if (isMounted.current) {
          setCandidats(data.data);
          setPagination(data.pagination);
          const source = forceRefresh ? 'live (forcé)' : 'live';
          setDataSource(source);
          await setCachedCandidats(cacheKey, { data: data.data, pagination: data.pagination });
          if (forceRefresh) {
            setToast({ show: true, message: 'Données actualisées', variant: 'success' });
            setTimeout(() => setToast({ show: false }), 3000);
          }
          setLoading(false);
          setRefreshing(false);
        }
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      // Gestion de l'annulation
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        if (isMounted.current) {
          // Réinitialiser les états de chargement pour éviter le spinner infini
          setLoading(false);
          setRefreshing(false);
        }
        return;
      }

      // Dernier recours : cache périmé
      const filterKey = `statut_${filters.statut || ''}_sexe_${filters.sexe || ''}`;
      const cacheKey = `page_${page}_search_${search}_${filterKey}`;
      const stale = await getStaleCandidats(cacheKey);
      if (stale && isMounted.current) {
        setCandidats(stale.data);
        setPagination(stale.pagination);
        setDataSource('cache (périmé)');
        setLoading(false);
        setRefreshing(false);
        setToast({ show: true, message: `Erreur – Utilisation du cache périmé : ${err.message}`, variant: 'warning' });
        setTimeout(() => setToast({ show: false }), 5000);
        return;
      }
      if (isMounted.current) {
        setToast({ show: true, message: err.message || 'Erreur de chargement', variant: 'danger' });
        setTimeout(() => setToast({ show: false }), 5000);
        setLoading(false);
        setRefreshing(false);
      }
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [filters, pagination.limit]);

  // --- Effet de chargement initial (déclenché une seule fois) ---
  useEffect(() => {
    fetchCandidats(1, '', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Effet pour les filtres (on recharge avec forceRefresh) ---
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const timeout = setTimeout(() => {
      fetchCandidats(1, searchTerm, true);
    }, 100);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // --- Gestion de la recherche (debounce) ---
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchCandidats(1, val, false);
    }, 400);
  };

  // --- Gestion des filtres ---
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // --- Rafraîchissement manuel ---
  const handleRefresh = () => {
    if (!loading && !refreshing) {
      fetchCandidats(pagination.currentPage, searchTerm, true);
    }
  };
  const handleForceRefresh = () => {
    if (!loading && !refreshing) {
      fetchCandidats(pagination.currentPage, searchTerm, true);
    }
  };

  // --- Formulaire (inchangé) ---
  const resetForm = () => {
    setForm({
      nom: '', prenom: '', date_naissance: '', sexe: '', email: '', tel: '',
      adresse: '', cvUrl: '', competence: '', statut: '',
      niveau_etude: '', experience: '', langues: ''
    });
    setCvFile(null);
    setFormErrors({});
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setCvFile(file);
    } else {
      setToast({ show: true, message: 'Veuillez sélectionner un fichier PDF', variant: 'warning' });
      setTimeout(() => setToast({ show: false }), 3000);
    }
  };

  const validateForm = () => {
    const errors = {};
    const required = ['nom', 'prenom', 'date_naissance', 'sexe', 'adresse', 'niveau_etude', 'experience'];
    required.forEach(field => {
      if (!form[field]?.trim()) errors[field] = `Le champ "${field}" est requis`;
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'Email invalide';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (candidat) => {
    setForm({
      nom: candidat.nom || '',
      prenom: candidat.prenom || '',
      date_naissance: candidat.date_naissance || '',
      sexe: candidat.sexe || '',
      email: candidat.email || '',
      tel: candidat.tel || '',
      adresse: candidat.adresse || '',
      cvUrl: candidat.cvUrl || '',
      competence: candidat.competence || '',
      statut: candidat.statut || '',
      niveau_etude: candidat.niveau_etude || '',
      experience: candidat.experience || '',
      langues: candidat.langues || ''
    });
    setCvFile(null);
    setEditingId(candidat.id);
    setFormErrors({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    Object.keys(form).forEach(key => {
      if (form[key] !== null && form[key] !== undefined) {
        formData.append(key, form[key]);
      }
    });
    if (cvFile) {
      formData.append('cvFile', cvFile);
    }
    if (editingId) {
      formData.append('id', editingId);
    }

    try {
      let res;
      if (editingId) {
        res = await fetch(`${API_URL}?id=${editingId}`, { method: 'PUT', body: formData });
      } else {
        res = await fetch(API_URL, { method: 'POST', body: formData });
      }
      const data = await res.json();
      if (!res.ok || data.status === 'error') throw new Error(data.message || 'Erreur');
      setToast({ show: true, message: editingId ? 'Candidat modifié ✓' : 'Candidat créé ✓', variant: 'success' });
      setTimeout(() => setToast({ show: false }), 3000);
      setShowModal(false);
      resetForm();
      await clearCache(`page_${pagination.currentPage}_search_${searchTerm}_statut_${filters.statut || ''}_sexe_${filters.sexe || ''}`);
      fetchCandidats(pagination.currentPage, searchTerm, true);
    } catch (err) {
      setToast({ show: true, message: err.message, variant: 'danger' });
      setTimeout(() => setToast({ show: false }), 5000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ Supprimer définitivement ce candidat ?')) return;
    try {
      const res = await fetch(`${API_URL}?id=${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || data.status === 'error') throw new Error(data.message || 'Erreur');
      setToast({ show: true, message: 'Candidat supprimé ✓', variant: 'success' });
      setTimeout(() => setToast({ show: false }), 3000);
      await clearCache(`page_${pagination.currentPage}_search_${searchTerm}_statut_${filters.statut || ''}_sexe_${filters.sexe || ''}`);
      fetchCandidats(pagination.currentPage, searchTerm, true);
    } catch (err) {
      setToast({ show: true, message: err.message, variant: 'danger' });
      setTimeout(() => setToast({ show: false }), 5000);
    }
  };

  // --- Rendu (avec indicateur de source) ---
  if (loading && candidats.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des candidats...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* styles inchangés (omis pour la brièveté) */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }
        * { box-sizing: border-box; }
        .candidat-list {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 30px;
          color: var(--text-primary);
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }
        .monetra-btn {
          padding: 10px 20px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .monetra-btn:hover { background: rgba(255,255,255,0.08); transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,0.2); }
        .monetra-btn:active { transform: translateY(0); }
        .monetra-btn.primary { background: var(--accent-blue); color: #fff; border: none; box-shadow: 0 6px 20px var(--accent-blue-glow); }
        .monetra-btn.primary:hover { background: var(--accent-dark-blue); box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4); }
        .monetra-btn.danger { background: rgba(239, 68, 68, 0.12); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.2); }
        .monetra-btn.danger:hover { background: rgba(239, 68, 68, 0.25); }
        .monetra-btn.icon-only { padding: 10px; border-radius: 30px; }
        .monetra-btn:disabled { opacity: 0.4; pointer-events: none; }
        .badge-source {
          font-size: 0.7rem;
          padding: 4px 14px;
          border-radius: 40px;
          background: rgba(59,130,246,0.12);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.12);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.12);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }
        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 20px;
          transition: all 0.3s ease;
          flex: 1;
          max-width: 400px;
        }
        .search-wrapper:focus-within { border-color: var(--accent-blue); box-shadow: 0 0 20px rgba(59, 130, 246, 0.08); }
        .search-wrapper input { background: transparent; border: none; color: var(--text-primary); outline: none; padding: 10px 0; flex: 1; font-size: 0.95rem; }
        .search-wrapper input::placeholder { color: var(--text-muted); }
        .filter-group {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }
        .filter-group select {
          padding: 8px 16px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 30px;
          color: var(--text-primary);
          outline: none;
          font-size: 0.85rem;
          cursor: pointer;
        }
        .filter-group select:focus { border-color: var(--accent-blue); }
        .glass-table { width: 100%; border-collapse: collapse; }
        .glass-table th { padding: 16px 12px; text-align: left; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--accent-blue); border-bottom: 1px solid rgba(255,255,255,0.04); font-weight: 600; }
        .glass-table td { padding: 14px 12px; border-bottom: 1px solid rgba(255,255,255,0.02); vertical-align: middle; }
        .glass-table tbody tr { transition: background 0.15s; }
        .glass-table tbody tr:hover { background: rgba(255,255,255,0.02); }
        .badge-statut {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .badge-statut.accepté { background: rgba(59, 130, 246, 0.15); color: var(--accent-blue); }
        .badge-statut.réfusée { background: rgba(239,68,68,0.15); color: #f87171; }
        .badge-statut.en-attente { background: rgba(251,191,36,0.15); color: #fbbf24; }
        .badge-statut.inactif { background: rgba(100,116,139,0.15); color: var(--text-muted); }
        .toast-container {
          position: fixed; top: 24px; right: 24px; z-index: 9999;
          display: flex; flex-direction: column; gap: 12px; max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex; align-items: center; gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent; border: none; color: var(--text-muted);
          cursor: pointer; font-size: 1.2rem; padding: 0 4px; margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { color: var(--text-primary); }
        .modal-glass .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px) saturate(1.2);
          -webkit-backdrop-filter: blur(30px) saturate(1.2);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          box-shadow: 0 40px 90px rgba(0,0,0,0.8), 0 0 0 1px rgba(59, 130, 246, 0.04) inset;
          overflow: hidden;
        }
        .modal-glass .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.8rem 2.2rem;
          background: rgba(0,0,0,0.15);
        }
        .modal-title-wrapper {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .modal-title-icon {
          width: 44px;
          height: 44px;
          border-radius: 14px;
          background: var(--accent-blue-dim);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
          color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .modal-title-text {
          font-size: 1.3rem;
          font-weight: 600;
          letter-spacing: -0.3px;
          background: linear-gradient(135deg, #e0e7ff 60%, #94a3b8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .modal-title-sub {
          font-size: 0.85rem;
          color: var(--text-muted);
          font-weight: 400;
          margin-top: 2px;
        }
        .modal-glass .modal-body { padding: 2.2rem 2.2rem 1.8rem; }
        .modal-glass .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.2rem 2.2rem;
          background: rgba(0,0,0,0.08);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        .form-group-premium {
          margin-bottom: 1.2rem;
        }
        .form-group-premium label {
          display: block;
          margin-bottom: 6px;
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--text-secondary);
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }
        .form-group-premium label .required {
          color: #f87171;
          margin-left: 2px;
        }
        .form-group-premium label .label-icon {
          color: var(--accent-blue);
          opacity: 0.6;
          margin-right: 6px;
        }
        .input-glass {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          transition: all 0.25s ease;
        }
        .input-glass:focus-within {
          border-color: var(--accent-blue);
          background: rgba(255,255,255,0.05);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08), 0 4px 20px rgba(59, 130, 246, 0.05);
        }
        .input-glass select,
        .input-glass input,
        .input-glass textarea {
          flex: 1;
          background: transparent;
          border: none;
          padding: 12px 16px;
          color: var(--text-primary);
          outline: none;
          font-family: inherit;
          font-size: 0.95rem;
        }
        .input-glass textarea { min-height: 60px; resize: vertical; }
        .input-glass select {
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
          cursor: pointer;
        }
        .input-glass select option { background: #0b1428; }
        .input-glass.error {
          border-color: #f87171;
          background: rgba(239,68,68,0.03);
          box-shadow: 0 0 0 3px rgba(239,68,68,0.06);
        }
        .input-glass .file-input-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 0 16px;
          width: 100%;
        }
        .input-glass .file-input-wrapper input[type="file"] {
          flex: 1;
          padding: 10px 0;
          color: var(--text-secondary);
        }
        .input-glass .file-input-wrapper input[type="file"]::file-selector-button {
          background: var(--accent-blue-dim);
          border: none;
          color: var(--accent-blue);
          padding: 6px 16px;
          border-radius: 30px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }
        .input-glass .file-input-wrapper input[type="file"]::file-selector-button:hover {
          background: rgba(59, 130, 246, 0.2);
        }
        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.8rem 1.5rem;
        }
        .form-grid .full-width { grid-column: span 2; }
        .error-text {
          color: #f87171;
          font-size: 0.75rem;
          margin-top: 4px;
          padding-left: 12px;
        }
        .btn-modal-cancel, .btn-modal-submit {
          padding: 10px 28px;
          border-radius: 30px;
          font-weight: 500;
          transition: all 0.25s ease;
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .btn-modal-cancel {
          background: rgba(255,255,255,0.04);
          color: var(--text-secondary);
          border: 1px solid rgba(255,255,255,0.06);
        }
        .btn-modal-cancel:hover {
          background: rgba(255,255,255,0.08);
          color: var(--text-primary);
          transform: translateY(-2px);
        }
        .btn-modal-submit {
          background: var(--accent-blue);
          color: #fff;
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.2);
        }
        .btn-modal-submit:hover {
          background: var(--accent-dark-blue);
          transform: translateY(-2px);
          box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
        }
        .btn-modal-submit:disabled { opacity: 0.5; pointer-events: none; }
        .flex-between { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
        .gap-2 { gap: 8px; }
        .mb-3 { margin-bottom: 1.5rem; }
        .mt-3 { margin-top: 1.5rem; }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .opacity-30 { opacity: 0.3; }
        @media (max-width: 768px) {
          .candidat-list { padding: 16px; }
          .form-grid { grid-template-columns: 1fr; }
          .form-grid .full-width { grid-column: span 1; }
          .modal-glass .modal-header { padding: 1.2rem 1.5rem; }
          .modal-glass .modal-body { padding: 1.5rem; }
          .modal-glass .modal-footer { padding: 1rem 1.5rem; flex-direction: column-reverse; }
          .modal-glass .modal-footer button { width: 100%; justify-content: center; }
          .glass-table td, .glass-table th { padding: 10px 8px; font-size: 0.8rem; }
          .filter-group select { padding: 6px 12px; font-size: 0.75rem; }
        }
      `}</style>

      <div className="candidat-list">
        {/* Header */}
        <div className="flex-between mb-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">👤 Candidats</h1>
            <p className="text-secondary m-0">Gestion complète des candidats</p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn primary" onClick={openAddModal}>
              <FontAwesomeIcon icon={faPlus} /> <span className="label">Nouveau</span>
            </button>
            <button className="monetra-btn" onClick={handleRefresh} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button className="monetra-btn" onClick={handleForceRefresh} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faBolt} /> Forcer
            </button>
          </div>
        </div>

        {/* Search + Filtres */}
        <div className="glass-card mb-3">
          <div className="flex-between">
            <div className="search-wrapper">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
              <input type="text" placeholder="Rechercher (nom, prénom, email, compétence...)" value={searchTerm} onChange={handleSearch} />
            </div>
            <div className="filter-group">
              <FontAwesomeIcon icon={faFilter} className="text-muted" />
              <select name="sexe" value={filters.sexe} onChange={handleFilterChange}>
                <option value="">Tous sexes</option>
                <option value="Homme">Homme</option>
                <option value="Femme">Femme</option>
              </select>
              <select name="statut" value={filters.statut} onChange={handleFilterChange}>
                <option value="">Tous statuts</option>
                <option value="Accepté">Accepté</option>
                <option value="Réfusée">Réfusée</option>
                <option value="En attente">En attente</option>
              </select>
            </div>
          </div>
          <div className="text-secondary small mt-2">
            <span className="fw-bold">{pagination?.totalRecords || 0}</span> candidats
          </div>
        </div>

        {/* Table */}
        <div className="glass-card p-0 overflow-hidden">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom</th>
                  <th>Prénom</th>
                  <th>Sexe</th>
                  <th>Email</th>
                  <th>Tél</th>
                  <th>Compétences</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {candidats.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-5 text-secondary">Aucun candidat trouvé.</td></tr>
                ) : (
                  candidats.map(item => (
                    <tr key={item.id}>
                      <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                      <td>{item.nom}</td>
                      <td>{item.prenom}</td>
                      <td><FontAwesomeIcon icon={faVenusMars} className="me-1 text-muted" />{item.sexe}</td>
                      <td><FontAwesomeIcon icon={faEnvelope} className="me-1 text-muted" />{item.email || '—'}</td>
                      <td><FontAwesomeIcon icon={faPhone} className="me-1 text-muted" />{item.tel || '—'}</td>
                      <td style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.competence || '—'}</td>
                      <td>
                        <span className={`badge-statut ${item.statut?.toLowerCase().replace(' ', '-') || 'inactif'}`}>
                          <FontAwesomeIcon icon={faCircle} className="me-1" style={{ fontSize: '0.5rem' }} />
                          {item.statut || 'Inactif'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Link to={`/profil-candidat/${item.id}`} className="monetra-btn icon-only text-decoration-none">
                            <FontAwesomeIcon icon={faUser} />
                          </Link>
                          <button className="monetra-btn icon-only" onClick={() => openEditModal(item)} title="Modifier">
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button className="monetra-btn icon-only danger" onClick={() => handleDelete(item.id)} title="Supprimer">
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 flex-between">
            <span className="text-secondary small">Page {pagination?.currentPage || 1} sur {pagination?.totalPages || 1}</span>
            <div className="d-flex gap-2">
              <button className="monetra-btn icon-only" disabled={!pagination || pagination.currentPage <= 1 || loading || refreshing} onClick={() => fetchCandidats(pagination.currentPage - 1, searchTerm)}>‹</button>
              <button className="monetra-btn icon-only" disabled={!pagination || pagination.currentPage >= pagination.totalPages || loading || refreshing} onClick={() => fetchCandidats(pagination.currentPage + 1, searchTerm)}>›</button>
            </div>
          </div>
        </div>
      </div>

      {/* Modale (inchangée) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered backdrop="static" className="modal-glass">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="modal-title-wrapper">
              <div className="modal-title-icon">
                <FontAwesomeIcon icon={editingId ? faEdit : faUserPlus} />
              </div>
              <div>
                <div className="modal-title-text">
                  {editingId ? 'Modifier le candidat' : 'Ajouter un candidat'}
                </div>
                <div className="modal-title-sub">
                  {editingId ? `#${editingId}` : 'Remplissez tous les champs obligatoires'}
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faUser} className="label-icon" />Nom <span className="required">*</span></label>
                <div className={`input-glass ${formErrors.nom ? 'error' : ''}`}>
                  <Form.Control type="text" name="nom" value={form.nom} onChange={handleInputChange} placeholder="Nom de famille" isInvalid={!!formErrors.nom} />
                </div>
                {formErrors.nom && <div className="error-text">{formErrors.nom}</div>}
              </div>
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faUser} className="label-icon" />Prénom <span className="required">*</span></label>
                <div className={`input-glass ${formErrors.prenom ? 'error' : ''}`}>
                  <Form.Control type="text" name="prenom" value={form.prenom} onChange={handleInputChange} placeholder="Prénom" isInvalid={!!formErrors.prenom} />
                </div>
                {formErrors.prenom && <div className="error-text">{formErrors.prenom}</div>}
              </div>
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faCalendarAlt} className="label-icon" />Date de naissance <span className="required">*</span></label>
                <div className={`input-glass ${formErrors.date_naissance ? 'error' : ''}`}>
                  <Form.Control type="date" name="date_naissance" value={form.date_naissance} onChange={handleInputChange} isInvalid={!!formErrors.date_naissance} />
                </div>
                {formErrors.date_naissance && <div className="error-text">{formErrors.date_naissance}</div>}
              </div>
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faVenusMars} className="label-icon" />Sexe <span className="required">*</span></label>
                <div className={`input-glass ${formErrors.sexe ? 'error' : ''}`}>
                  <Form.Select name="sexe" value={form.sexe} onChange={handleInputChange} isInvalid={!!formErrors.sexe}>
                    <option value="">Sélectionner</option>
                    <option className="bg-dark" value="Homme">Homme</option>
                    <option className="bg-dark" value="Femme">Femme</option>
                  </Form.Select>
                </div>
                {formErrors.sexe && <div className="error-text">{formErrors.sexe}</div>}
              </div>
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faEnvelope} className="label-icon" />Email</label>
                <div className={`input-glass ${formErrors.email ? 'error' : ''}`}>
                  <Form.Control type="email" name="email" value={form.email} onChange={handleInputChange} placeholder="exemple@domaine.com" isInvalid={!!formErrors.email} />
                </div>
                {formErrors.email && <div className="error-text">{formErrors.email}</div>}
              </div>
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faPhone} className="label-icon" />Téléphone</label>
                <div className="input-glass">
                  <Form.Control type="text" name="tel" value={form.tel} onChange={handleInputChange} placeholder="Numéro de téléphone" />
                </div>
              </div>
              <div className="form-group-premium full-width">
                <label><FontAwesomeIcon icon={faMapMarkerAlt} className="label-icon" />Adresse <span className="required">*</span></label>
                <div className={`input-glass ${formErrors.adresse ? 'error' : ''}`}>
                  <Form.Control type="text" name="adresse" value={form.adresse} onChange={handleInputChange} placeholder="Adresse complète" isInvalid={!!formErrors.adresse} />
                </div>
                {formErrors.adresse && <div className="error-text">{formErrors.adresse}</div>}
              </div>
              <div className="form-group-premium full-width">
                <label><FontAwesomeIcon icon={faFilePdf} className="label-icon" />CV (PDF)</label>
                <div className="input-glass">
                  <div className="file-input-wrapper">
                    <Form.Control type="file" accept=".pdf" onChange={handleFileChange} />
                    {editingId && form.cvUrl && !cvFile && (
                      <a href={form.cvUrl} target="_blank" rel="noopener noreferrer" className="text-primary small">
                        <FontAwesomeIcon icon={faEye} className="me-1" /> Voir CV
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-group-premium full-width">
                <label><FontAwesomeIcon icon={faBriefcase} className="label-icon" />Compétences</label>
                <div className="input-glass">
                  <Form.Control as="textarea" name="competence" value={form.competence} onChange={handleInputChange} placeholder="Liste des compétences (ex: PHP, React, SQL...)" rows={2} />
                </div>
              </div>
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faGraduationCap} className="label-icon" />Niveau d'étude <span className="required">*</span></label>
                <div className={`input-glass ${formErrors.niveau_etude ? 'error' : ''}`}>
                  <Form.Control type="text" name="niveau_etude" value={form.niveau_etude} onChange={handleInputChange} placeholder="Bac+2, Master..." isInvalid={!!formErrors.niveau_etude} />
                </div>
                {formErrors.niveau_etude && <div className="error-text">{formErrors.niveau_etude}</div>}
              </div>
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faBriefcase} className="label-icon" />Expérience <span className="required">*</span></label>
                <div className={`input-glass ${formErrors.experience ? 'error' : ''}`}>
                  <Form.Control type="text" name="experience" value={form.experience} onChange={handleInputChange} placeholder="3 ans, 5 ans..." isInvalid={!!formErrors.experience} />
                </div>
                {formErrors.experience && <div className="error-text">{formErrors.experience}</div>}
              </div>
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faLanguage} className="label-icon" />Langues</label>
                <div className="input-glass">
                  <Form.Select name="langues" value={form.langues} onChange={handleInputChange}>
                    <option className="bg-dark" value="">Sélectionner une langue</option>
                    <option className="bg-dark" value="Francais">Français</option>
                    <option className="bg-dark" value="Francais Avancé">Français Avancé</option>
                    <option className="bg-dark" value="Anglais">Anglais</option>
                    <option className="bg-dark" value="Anglais Avancé">Anglais Avancé</option>
                    <option className="bg-dark" value="Espaniol">Espagnol</option>
                    <option className="bg-dark" value="Portugais">Portugais</option>
                    <option className="bg-dark" value="Mandarin">Mandarin</option>
                  </Form.Select>
                </div>
              </div>
              <div className="form-group-premium">
                <label><FontAwesomeIcon icon={faIdCard} className="label-icon" />Statut</label>
                <div className="input-glass">
                  <Form.Select name="statut" value={form.statut} onChange={handleInputChange}>
                    <option className="bg-dark" value="">Non défini</option>
                    <option className="bg-dark" value="Accepté">Accepté</option>
                    <option className="bg-dark" value="Réfusée">Réfusée</option>
                    <option className="bg-dark" value="En attente">En attente</option>
                  </Form.Select>
                </div>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn-modal-cancel" onClick={() => setShowModal(false)}>
            <FontAwesomeIcon icon={faTimes} /> Annuler
          </button>
          <button className="btn-modal-submit" onClick={handleSubmit}>
            <FontAwesomeIcon icon={editingId ? faCheckCircle : faPlus} />
            {editingId ? 'Mettre à jour' : 'Ajouter'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageCandidats;